/**
 * page-chat-client.ts — thin client for /api/zeq/agent/page-chat.
 *
 * The endpoint runs the math FIRST: selectIntentAwareOperators picks
 * operators, computeZeqState runs the 7-step protocol, the result is
 * injected into the LLM's system prompt as `[BASELINE COMPUTATION]`.
 * The LLM responds GROUNDED in real physics; calling 7 server-side
 * tools (spawn_genetic_agent, run_zeq_compute, derive_operator, etc.)
 * if it needs to.
 *
 * Response shape (zeqAgent.ts:2598–2620):
 *   {
 *     ok: true,
 *     slug,
 *     active_agent_id,
 *     active_agent_zid,
 *     response: string,                ← the LLM's text reply
 *     model: string,
 *     zeqond: number,
 *     tools_used: [{ name, arguments, result }],
 *     zeq: {
 *       operators: [{ id, name, domain }],
 *       master_sum, phase, zeqond, masterEquation, R_t, precision
 *     },
 *     zeqProof: string                 ← sha256 over canonical proof input
 *   }
 *
 * It is a SINGLE res.json — NOT a stream. The previous SSE parser was
 * silently dropping the body; that's why replies looked empty.
 */

export interface PageChatTurn {
  role: "user" | "assistant";
  content: string;
}

export interface PageChatRequest {
  slug: string;
  message: string;
  history?: PageChatTurn[];
  agent_id?: string;
}

export interface PageChatOperator {
  id: string;
  name: string;
  domain: string;
}

export interface PageChatToolUse {
  name: string;
  arguments: Record<string, unknown>;
  result: unknown;
}

/**
 * Shape of the live /api/zeq/agent/page-chat response. Field names match
 * the server output verbatim (see shared/api-core/src/routes/zeqAgent.ts
 * around line ~2598). The framework's "master sum" is exposed as `R_t`
 * (R(t)) and the proof hash as `proof_digest`. `zeqond` is a top-level
 * field, not inside `zeq`.
 *
 * Both the type and the orb-panel renderer were silently reading
 * non-existent fields (master_sum / zeq.zeqond / zeqProof) and rendering
 * em-dashes — fixed to read the actual API shape.
 */
export interface PageChatResponse {
  ok: boolean;
  slug?: string;
  active_agent_id?: string | null;
  active_agent_zid?: string | null;
  response: string;
  model?: string;
  zeqond?: number; // top-level; canonical Zeqond number for this turn
  tools_used: PageChatToolUse[];
  zeq?: {
    operators: PageChatOperator[];
    /** Master sum / R(t) from the framework. Server emits as R_t. */
    R_t?: number;
    phase?: number;
    precision?: number;
    pulse_hz?: number;
    domain?: string;
    /** Server-side ZeqProof — sha256 over canonical proof input. */
    proof_digest?: string;
    verified?: boolean;
    /** Multi-line typeset master equation (operator chain). */
    master_equation?: string;
  };
  /** Legacy/optional top-level proof — kept for backward-compat. */
  zeqProof?: string;
  error?: string;
  code?: string;
}

/**
 * POST a chat turn and return the parsed JSON response. Throws only on
 * transport errors. Soft errors (provider missing, OWNER_NO_CREDENTIAL,
 * etc.) come through as { ok: false, error, code } so the caller can
 * render them in-feed.
 */
export async function pageChat(req: PageChatRequest): Promise<PageChatResponse> {
  const res = await fetch("/api/zeq/agent/page-chat", {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  });

  // Read the body as text first so we can show it on parse failure.
  const text = await res.text();
  if (!res.ok) {
    let parsed: PageChatResponse | null = null;
    try { parsed = JSON.parse(text) as PageChatResponse; } catch { /* */ }
    return parsed ?? {
      ok: false,
      response: "",
      tools_used: [],
      error: `page-chat ${res.status}: ${text.slice(0, 280)}`,
    };
  }
  try {
    return JSON.parse(text) as PageChatResponse;
  } catch (err) {
    return {
      ok: false,
      response: "",
      tools_used: [],
      error: `unparseable page-chat response: ${(err as Error)?.message ?? String(err)}`,
    };
  }
}
